import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/pages/admin/AdminLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const empty = {
  name: "",
  description: "",
  price: "",
  discount_percent: 0,
  category: "women",
  brand: "",
  material: "",
  sizes: [],
  colors: [],
  images: [],
  stock: 0,
  featured: false,
};

export default function AdminProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(empty);
  const [sizeInput, setSizeInput] = useState("");
  const [colorInput, setColorInput] = useState("");
  const [imgInput, setImgInput] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isEdit) {
      api.get(`/products/${id}`).then((r) => setForm(r.data));
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addTo = (key, value, setter) => {
    if (!value.trim()) return;
    setForm((f) => ({ ...f, [key]: [...(f[key] || []), value.trim()] }));
    setter("");
  };
  const removeFrom = (key, idx) => setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        discount_percent: Number(form.discount_percent) || 0,
        stock: Number(form.stock) || 0,
      };
      if (isEdit) {
        await api.put(`/products/${id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      navigate("/admin/products");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout title={isEdit ? "Edit Product" : "New Product"}>
      <form onSubmit={save} className="max-w-3xl space-y-6" data-testid="admin-product-form">
        <F label="Name">
          <input type="text" required value={form.name} onChange={(e) => set("name", e.target.value)} className={input} data-testid="pf-name" />
        </F>
        <F label="Description">
          <textarea rows="4" value={form.description} onChange={(e) => set("description", e.target.value)} className={`${input} h-auto py-2 resize-y`} data-testid="pf-description" />
        </F>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <F label="Price (INR)">
            <input type="number" required min="0" step="1" value={form.price} onChange={(e) => set("price", e.target.value)} className={input} data-testid="pf-price" />
          </F>
          <F label="Discount %">
            <input type="number" min="0" max="90" step="1" value={form.discount_percent} onChange={(e) => set("discount_percent", e.target.value)} className={input} data-testid="pf-discount" />
          </F>
          <F label="Stock">
            <input type="number" min="0" step="1" value={form.stock} onChange={(e) => set("stock", e.target.value)} className={input} data-testid="pf-stock" />
          </F>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <F label="Category">
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className={input} data-testid="pf-category">
              <option value="women">Women</option>
              <option value="men">Men</option>
              <option value="accessories">Accessories</option>
              <option value="unisex">Unisex</option>
            </select>
          </F>
          <F label="Brand">
            <input value={form.brand} onChange={(e) => set("brand", e.target.value)} className={input} data-testid="pf-brand" />
          </F>
          <F label="Material">
            <input value={form.material} onChange={(e) => set("material", e.target.value)} className={input} data-testid="pf-material" />
          </F>
        </div>

        <ChipEditor label="Sizes" items={form.sizes} onRemove={(i) => removeFrom("sizes", i)} inputValue={sizeInput} onInput={setSizeInput} onAdd={() => addTo("sizes", sizeInput, setSizeInput)} testKey="size" />
        <ChipEditor label="Colors" items={form.colors} onRemove={(i) => removeFrom("colors", i)} inputValue={colorInput} onInput={setColorInput} onAdd={() => addTo("colors", colorInput, setColorInput)} testKey="color" />

        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6b6b6b] mb-2">Product Images</div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <ImageUploader
              onUploaded={(url) => setForm((f) => ({ ...f, images: [...(f.images || []), url] }))}
              testId="pf-image-upload"
            />
            <span className="text-xs text-[#6b6b6b]">or paste a URL:</span>
            <div className="flex gap-2 flex-1 min-w-[280px]">
              <input
                value={imgInput}
                onChange={(e) => setImgInput(e.target.value)}
                placeholder="https://…"
                className={`${input} flex-1`}
                data-testid="pf-image-input"
              />
              <button
                type="button"
                onClick={() => addTo("images", imgInput, setImgInput)}
                className="btn-ink text-sm"
                data-testid="pf-image-add"
              >
                Add
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {form.images.map((im, i) => (
              <div key={i} className="relative aspect-square bg-[#faf7f2] overflow-hidden border border-[#e0d8c8] rounded-md">
                <img src={im} className="w-full h-full object-cover" alt="" />
                <button
                  type="button"
                  onClick={() => removeFrom("images", i)}
                  className="absolute top-1 right-1 bg-black/70 hover:bg-black text-white w-6 h-6 flex items-center justify-center rounded-full"
                  data-testid={`pf-image-remove-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={form.featured}
            onChange={(e) => set("featured", e.target.checked)}
            className="w-4 h-4 accent-[#c8532e]"
            data-testid="pf-featured"
          />
          Feature on homepage
        </label>

        <div className="flex gap-4">
          <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="pf-submit">
            {busy ? "Saving…" : isEdit ? "Update Product" : "Create Product"}
          </button>
          <button type="button" onClick={() => navigate("/admin/products")} className="btn-outline text-sm" data-testid="pf-cancel">
            Cancel
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

const input = "w-full h-10 bg-white border border-[#e0d8c8] focus:border-[#c8532e] focus:ring-2 focus:ring-[#c8532e]/30 rounded-md outline-none px-3 text-sm";

function F({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6b6b6b]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ChipEditor({ label, items, onRemove, inputValue, onInput, onAdd, testKey }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6b6b6b] mb-2">{label}</div>
      <div className="flex gap-2 mb-3">
        <input
          value={inputValue}
          onChange={(e) => onInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAdd();
            }
          }}
          className={input}
          placeholder={`Add ${label.toLowerCase()}`}
          data-testid={`pf-${testKey}-input`}
        />
        <button type="button" onClick={onAdd} className="btn-ink text-sm" data-testid={`pf-${testKey}-add`}>
          Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-2 px-3 h-8 border border-[#1a1d29] rounded-full text-xs">
            {it}
            <button type="button" onClick={() => onRemove(i)} data-testid={`pf-${testKey}-remove-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
